import * as _ from 'lodash';
import Api from 'services/Api';
import store from 'store/index';
import Validator from 'services/Validator';
import {previousValid} from 'store/generators/step';

export const mutationTypes = {
    RESET: 'RESET',
    UPDATE: 'UPDATE',
    SERVER_UPDATE: 'SERVER_UPDATE',
    UPDATE_DISCOUNT_RATE: 'UPDATE_DISCOUNT_RATE'
};

// Initial State
const state = {
    claimantName: null,
    defendantName:  {
        id: null,
        name: null,
        type: null
    },
    claimantSolicitor: {
        id: null,
        name: null,
        type: null
    },
    claimantReference: null,
    yourReference: null,
    claimNarrative: null,
    diseaseOrAccident: null,
    accidentType: null,
    otherAccidentType: null,
    diseaseType: null,
    otherDiseaseType: null,
    proceduralStatus: 'pre-lit',
    serviceOfProceedings: null,
    assumedSettlement: null,
    discountRate: -0.25,
    levelOfEducation: null,
    disabled: 0,
    employedAtDeath: '',
};

/**
 * Validation Rules.
 * 
 * @type {Object}
 */
let rules = {
    'claimantName' : {type: 'text', rules: 'required'},
    'defendantName' : {type: 'text', rules: 'required'},
    'claimantSolicitor' : {type: 'text', rules: 'required'},
    'claimantReference' : {type: 'text', rules: 'required'},
    'yourReference' : {type: 'text', rules: 'required'},
    'claimNarrative' : {type: 'text', rules: 'required'},
    // 'serviceOfProceedings' : {type: 'date', rules: 'required'}, not required for pre-lit claims
    'assumedSettlement' : {type: 'date', rules: 'required'},
};

// Getters
const getters = {
    value: state => key => state[key],
    discountRate: state => state.discountRate,
};

// Actions
const actions = {
    /**
     * Update single value
     *
     * @param commit
     * @param key
     * @param value
     */
    update({ commit, dispatch }, { key, value }) {
        commit(mutationTypes.UPDATE, { key, value });

        if (key === 'assumedSettlement') {
            dispatch('checkToUpdateLifeExpectancy');
            store.dispatch('fatal/estate/deceased/determineAgeOfDeathToSettlement');
            store.dispatch('fatal/estate/deceased/determineAgeAtSettlement');
        }
        if (key === 'discountRate') {
            return dispatch('updateDiscountRate', value);
        }
    },

    /**
     * Save the step by submitting to the server.
     *
     * @param dispatch
     * @param payload
     */
    save({ dispatch }, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        return Api.claims.save(payload).then((response) => {
            return response;
        });
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param commit
     * @param step
     */
    load({ commit }, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        commit(mutationTypes.SERVER_UPDATE, data);
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process() {
        store.dispatch('fatal/estate/step/setStepNumberValid', {
            stepNo: 0,
            valid: true, // needs updating with real validity
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({ rootState }) {
        let updateRules = _.clone(rules);
        if (rootState.user.user.hideNames) {
            delete updateRules['claimantName'];
        }
        store.dispatch('fatal/estate/step/setStepNumberValid', {
            stepNo: 0,
            valid: previousValid(rootState, 0) && (new Validator(state, updateRules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({ commit }) {
        store.dispatch('fatal/estate/step/setStepNumberValid', {
            stepNo: 0,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Update Discount Rate
     *
     * @param commit
     * @param dispatch
     * @param rate
     */
    updateDiscountRate({ commit, dispatch }, rate) {
        commit(mutationTypes.UPDATE_DISCOUNT_RATE, rate);
    },

    /**
     * Check to see if the life expectancy needs updating
     *
     * @param dispatch
     */
    checkToUpdateLifeExpectancy({dispatch}) {
        let lifeExpectancy = store.getters['fatal/estate/deceased/theDeceasedLifeExpectancy'];
        if (lifeExpectancy.dateOfDeath !== null)
         {
            dispatch('fatal/estate/deceased/updateTheDeceasedLifeExpectancy', {lifeExpectancy}, { root: true });
        }
    },
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE](state, {key, value}) {
        state[key] = value;
    },
    [mutationTypes.UPDATE_DISCOUNT_RATE](state, rate) {
        state.discountRate = parseFloat(rate);
    },
    [mutationTypes.SERVER_UPDATE](state, data) {
        state.claimantName = data.claimantName;
        state.claimantSolicitor = data.claimantSolicitor;
        state.claimantReference = data.claimantReference;
        state.yourReference = data.yourReference;
        state.claimNarrative = data.claimNarrative;
        state.diseaseOrAccident = data.diseaseOrAccident;
        state.diseaseType = data.diseaseType;
        state.otherDiseaseType = data.otherDiseaseType;
        state.accidentType = data.accidentType;
        state.otherAccidentType = data.otherAccidentType;
        state.defendantName = data.defendantName;
        state.proceduralStatus =  data.proceduralStatus;
        state.serviceOfProceedings = data.serviceOfProceedings;
        state.assumedSettlement = data.assumedSettlement;
        state.discountRate = data.discountRate;
        state.levelOfEducation = data.levelOfEducation;
        state.disabled = data.disabled;
        state.employedAtDeath = data.employedAtDeath;
    },
    [mutationTypes.RESET](state) {
        state.claimantName = null;
        state.claimantReference = null;
        state.yourReference = null;
        state.claimNarrative = null;
        state.diseaseOrAccident = null;
        state.diseaseType = null;
        state.otherDiseaseType = null;
        state.accidentType = null;
        state.otherAccidentType = null;
        state.proceduralStatus =  'pre-lit';
        state.serviceOfProceedings = null;
        state.assumedSettlement = null;
        state.discountRate =  -0.25;
        state.levelOfEducation = null;
        state.disabled = 0;
        state.employedAtDeath = '';
        state.defendantName.id = null;
        state.defendantName.name = null;
        state.defendantName.type = null;
        state.claimantSolicitor.id = null;
        state.claimantSolicitor.name = null;
        state.claimantSolicitor.type = null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/estate/claimDetails.js